import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Server, Apple, Shield, Building2, Cpu, Globe } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const expertiseData = [
  {
    icon: Server,
    title: "Systems Architecture",
    items: ["Infrastructure design & deployment", "Scalable systems architecture", "Enterprise solutions", "Cloud & hybrid environments"],
  },
  {
    icon: Apple,
    title: "Apple Ecosystem",
    items: ["macOS infrastructure", "Enterprise deployment (MDM)", "Hardware engineering", "Advanced diagnostics"],
  },
  {
    icon: Shield,
    title: "Cybersecurity",
    items: ["Penetration testing", "Vulnerability detection", "Security architecture", "Risk assessment"],
  },
  {
    icon: Building2,
    title: "Real Estate Tech",
    items: ["Property management systems", "CRM & booking platforms", "Operational automation", "Investment infrastructure"],
  },
  {
    icon: Cpu,
    title: "Digital Infrastructure",
    items: ["POS & payment systems", "Workflow automation", "API integrations", "Business tools deployment"],
  },
  {
    icon: Globe,
    title: "International Operations",
    items: ["Multi-country management", "Technology ecosystem expansion", "Distribution networks", "Strategic partnerships"],
  },
];

export const Expertise = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header
      gsap.from(headerRef.current, {
        y: 50,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      });

      // Cards with stagger
      cardsRef.current.forEach((card, index) => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        });

        // Card entrance
        tl.from(card, {
          y: 80,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
          delay: index * 0.1
        });

        // Icon bounce
        const icon = card.querySelector('.card-icon');
        tl.from(icon, {
          scale: 0,
          rotation: -180,
          duration: 0.5,
          ease: "back.out(2)"
        }, "-=0.4");

        // Title
        const title = card.querySelector('.card-title');
        tl.from(title, {
          x: -30,
          opacity: 0,
          duration: 0.4,
          ease: "power2.out"
        }, "-=0.3");

        // List items
        const items = card.querySelectorAll('.list-item');
        tl.from(items, {
          x: -20,
          opacity: 0,
          duration: 0.3,
          stagger: 0.05,
          ease: "power2.out"
        }, "-=0.2");

        // Hover animation
        card.addEventListener('mouseenter', () => {
          gsap.to(card, {
            y: -5,
            duration: 0.3,
            ease: "power2.out"
          });
          gsap.to(icon, {
            scale: 1.1,
            duration: 0.3,
            ease: "power2.out"
          });
        });

        card.addEventListener('mouseleave', () => {
          gsap.to(card, {
            y: 0,
            duration: 0.3,
            ease: "power2.out"
          });
          gsap.to(icon, {
            scale: 1,
            duration: 0.3,
            ease: "power2.out"
          });
        });
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="expertise"
      data-testid="expertise-section"
      className="py-24 md:py-32 bg-concrete/50 border-t border-border/50"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <div ref={headerRef} className="mb-16 md:mb-20">
          <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-medium block mb-4">
            03
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground">
            Core Expertise
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-border/50">
          {expertiseData.map((expertise, index) => (
            <div
              key={expertise.title}
              ref={(el) => cardsRef.current[index] = el}
              data-testid={`expertise-card-${index}`}
              className="group relative p-8 md:p-10 bg-transparent hover:bg-obsidian/5 transition-colors duration-300 border-b border-r border-border/50 cursor-pointer"
            >
              {/* Icon */}
              <div className="card-icon mb-6">
                <expertise.icon
                  className="w-8 h-8 text-foreground/80 group-hover:text-foreground transition-colors"
                  strokeWidth={1.5}
                />
              </div>

              {/* Title */}
              <h3 className="card-title font-heading text-lg md:text-xl font-semibold text-foreground mb-5">
                {expertise.title}
              </h3>

              {/* Items */}
              <ul className="space-y-2.5">
                {expertise.items.map((item, idx) => (
                  <li key={idx} className="list-item text-muted-foreground text-sm flex items-center gap-3">
                    <span className="w-1 h-1 bg-steel rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>

              {/* Hover line */}
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-foreground group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Expertise;
